'use client';

import type { ReactNode } from 'react';

import { ThinkingDot } from '@/components/ui/status-indicator';
import { useRotatingIcon } from '@/hooks/use-rotating-icon';
import { isStreamingStepId } from '@/lib/assistant-cloud-local/step-ids';
import { TEXT_STEP_ICONS, type ToolIconConfig } from '@/lib/constants/tool-ui-icons';

import { isThinkingRowId } from './trail-records';

export type TrailIconRenderer = (row: { stepId: string }) => ReactNode | undefined;

// A stable id per icon-set reference. When a step swaps its icon set mid-flight, keying the rotator
// on this forces a clean remount so `useRotatingIcon` re-seeds on the new set instead of showing a
// stale icon from the old one.
const iconSetIds = new WeakMap<object, number>();
let nextIconSetId = 0;
function iconSetKey(icons: ToolIconConfig[]): number {
  let id = iconSetIds.get(icons);
  if (id === undefined) {
    id = nextIconSetId++;
    iconSetIds.set(icons, id);
  }
  return id;
}

/** Tenant-configured rotating icon used as the in-progress indicator on
 *  trail rows whose tool has an `icons` config. Crossfades between icons
 *  on a fixed cadence (handled by `useRotatingIcon`). */
export function RotatingTrailIcon({ icons }: { icons: ToolIconConfig[] }) {
  const { Icon, flip, isTransitioning } = useRotatingIcon({ icons });
  if (!Icon) return null;
  return (
    <span
      className={`inline-flex transition-all duration-500 ease-in-out ${
        isTransitioning ? 'scale-50 opacity-0' : 'scale-100 opacity-100'
      }`}
    >
      <Icon
        aria-hidden="true"
        className={`h-5 w-5 text-(--trail-icon-color,var(--primary)) motion-reduce:animate-none ${flip ? '-scale-x-100' : ''}`}
      />
    </span>
  );
}

/**
 * Builds the in-progress-icon renderer used by both the live activity-trail
 * and its storybook preview: synthetic thinking-gap / streaming-text rows
 * render the `<ThinkingDot />`; tool rows render their tenant-configured
 * rotating icon when one is registered for the step id.
 */
export function createTrailInProgressIconRenderer({
  iconsByStepId,
}: {
  iconsByStepId: ReadonlyMap<string, ToolIconConfig[]>;
}): TrailIconRenderer {
  return function renderInProgressIcon({ stepId }) {
    if (isThinkingRowId(stepId)) {
      return <ThinkingDot />;
    }
    if (isStreamingStepId(stepId)) {
      return <RotatingTrailIcon icons={TEXT_STEP_ICONS} />;
    }
    const icons = iconsByStepId.get(stepId);
    if (!icons || icons.length === 0) return undefined;
    return <RotatingTrailIcon key={iconSetKey(icons)} icons={icons} />;
  };
}
